import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Award, CalendarHeart, Video, Baby, Users } from "lucide-react";
import { ToothMascot } from "@/components/site/ToothMascot";
import { ToothExplorer } from "./ToothExplorer";
import { AgeGuide } from "./AgeGuide";

const ModeToggle = ({ mode, setMode }) => (
  <div className="inline-flex rounded-full border-2 border-slate-800 bg-white p-1" data-testid="hero-mode-toggle">
    {[["kids", "I'm a kid", Baby], ["parents", "I'm a parent", Users]].map(([v, l, Icon]) => (
      <button key={v} onClick={() => setMode(v)} data-testid={`hero-mode-${v}`}
        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-colors ${mode === v ? "bg-teal-600 text-white" : "text-slate-600 hover:text-teal-700"}`}>
        <Icon className="h-4 w-4" />{l}
      </button>
    ))}
  </div>
);

const fade = (d) => ({ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { delay: d, duration: 0.5 } });

export const InteractiveHero = () => {
  const [mode, setMode] = useState("kids");
  const [stage, setStage] = useState(2);
  const kids = mode === "kids";
  return (
    <section id="top" className="relative overflow-hidden dot-bg" data-testid="hero-section">
      <div className="absolute -left-24 top-20 h-72 w-72 rounded-full bg-[#ccfbf1] blur-3xl" />
      <div className="absolute -right-20 bottom-0 h-80 w-80 rounded-full bg-[#fef3c7] blur-3xl" />
      <div className="relative mx-auto grid max-w-7xl gap-10 px-6 pb-16 pt-10 lg:grid-cols-[1fr_1.05fr] lg:px-8 lg:pt-14">
        <div className="flex flex-col">
          <motion.div {...fade(0.1)}><ModeToggle mode={mode} setMode={setMode} /></motion.div>
          <motion.h1 {...fade(0.2)} className="mt-6 text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-title">
            {kids ? <>Let's explore your <span className="text-teal-600">super</span> <span className="text-[#ff6b6b]">smile!</span></>
              : <>Know your child's <span className="text-teal-600">smile</span>, <span className="text-[#ff6b6b]">stage by stage.</span></>}
          </motion.h1>
          <motion.p {...fade(0.3)} className="mt-4 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg">
            {kids ? "Tap the teeth in the mouth to meet them, then pick your age to see what's happening in your smile."
              : "Tap a tooth to see when it erupts and sheds, and pick an age for guideline-based care tips from pediatric dentist Dr. Anuj Shah's clinic."}
          </motion.p>
          <motion.div {...fade(0.4)} className="mt-6"><AgeGuide stage={stage} setStage={setStage} mode={mode} /></motion.div>
          <motion.div {...fade(0.5)} className="mt-6 flex flex-wrap gap-3">
            <Link to="/book?type=clinic" data-testid="hero-book-clinic-button"
              className="btn-pop shadow-chunky-coral inline-flex items-center gap-2 rounded-full bg-[#ff6b6b] px-6 py-3.5 font-bold text-white"><CalendarHeart className="h-5 w-5" />Book a clinic visit</Link>
            <Link to="/book?type=online" data-testid="hero-online-consult-button"
              className="btn-pop inline-flex items-center gap-2 rounded-full border-2 border-slate-800 bg-white px-6 py-3.5 font-bold text-slate-800 hover:bg-[#fef7e5]"><Video className="h-5 w-5" />Online consultation</Link>
          </motion.div>
          <div className="mt-5 flex flex-wrap gap-4 text-xs font-bold text-slate-600">
            <span className="flex items-center gap-1"><Star className="h-4 w-4 fill-[#f5b82e] text-[#f5b82e]" />5.0 on Google (36 reviews)</span>
            <span className="flex items-center gap-1"><Award className="h-4 w-4 text-[#f5b82e]" />MDS Pediatric Dentist</span>
          </div>
        </div>
        <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3, duration: 0.6 }}
          className="relative rounded-[2.5rem] border-2 border-slate-800/5 bg-[#ebfbfa] p-5 sm:p-8">
          <div className="animate-floaty absolute -right-3 -top-6 w-20 sm:w-24"><ToothMascot mood={kids ? "wow" : "happy"} className="w-full drop-shadow-lg" /></div>
          <p className="font-display text-sm font-semibold uppercase tracking-widest text-teal-700">Tooth explorer</p>
          <div className="mt-3"><ToothExplorer mode={mode} /></div>
        </motion.div>
      </div>
    </section>
  );
};
